import { ResponsiveBar } from '@nivo/bar';
import { ResponsivePie } from '@nivo/pie';
import React, { useMemo, useState } from 'react';
import { TwTheme } from '../../Tw';
import TitleSection from '../components/layout/TitleSection';
import { Stat } from '../components/stats/Stat';
import { StatGroup } from '../components/stats/StatGroup';
import { Table } from '../components/table/Table';

import { useTranslation } from 'react-i18next';
import { formatInteger } from '../common/formatHelper';
import ButtonSwitchMulti from '../components/actions/ButtonSwitchMulti';
import { TableHead } from '../components/table/TableHead';
import { TableRow } from '../components/table/TableRow';
import { TableTd } from '../components/table/TableTd';
import { TableTh } from '../components/table/TableTh';
import ChartTooltip from './panels/ChartTooltip';
import {
  ABC_COLORS,
  getWeeksFromData,
  getXYZClassification,
  SKUData,
  XYZ_COLORS,
} from './sku-profiling';
import { useSKU } from './SKUContext';
import SKUListView from './SKUListView';
import { SKUMatrixControl } from './SKUMatrixControl';

interface GroupMetrics {
  count: number;
  avgLinesAccuracy: number;
  avgQuantityAccuracy: number;
  avgAbcAccuracy: number;
  avgXyzAccuracy: number;
  totalLines: number;
  totalQuantity: number;
}

interface TotalMetrics extends GroupMetrics {
  percentage: number;
}

const categoryABCXYZ = TwTheme.extend.colors.categoryABCXYZ;

const SKUGroupAnalysisView: React.FC = () => {
  const { t } = useTranslation('app');
  const { selectedSKU, setSelectedSKU, skuData } = useSKU();

  console.log('Initial SKU data sample:', skuData?.[0]);

  const [selectedABC, setSelectedABC] = useState<string | null>(null);
  const [selectedXYZ, setSelectedXYZ] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'sku' | 'lines'>('sku');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [metricsView, setMetricsView] = useState<'table' | 'chart'>('table');

  // Calculate metrics for any filtered subset of SKUs
  const calculateMetrics = (skus: SKUData[]): TotalMetrics => {
    console.log('Starting calculateMetrics with SKUs:', skus?.length);

    if (!skus?.length) {
      return {
        count: 0,
        avgLinesAccuracy: 0,
        avgQuantityAccuracy: 0,
        avgAbcAccuracy: 0,
        avgXyzAccuracy: 0,
        totalLines: 0,
        totalQuantity: 0,
        percentage: 0,
      };
    }

    const validSkus = skus.filter(sku => sku !== null);
    // console.log('Valid SKUs:', validSkus.length);

    // Calculate accuracies for each SKU
    const accuracies = validSkus.map(sku => {
      const weeks = getWeeksFromData(sku);
      // console.log('Processing SKU:', sku.sku, 'Weeks:', weeks);

      let linesAccuracy = 0;
      let validWeeksCount = 0;

      weeks.forEach(week => {
        // Lines accuracy
        const actualLines = Number(sku[`w${week}_lines`]) || 0;
        const forecastLines = Number(sku[`w${week}_fc_lines`]) || 0;

        // console.log(`Week ${week}:`, {
        //   actual: actualLines,
        //   forecast: forecastLines,
        // });

        if (actualLines > 0) {
          const weekAccuracy =
            1 - Math.abs(actualLines - forecastLines) / actualLines;
          linesAccuracy += weekAccuracy;
          validWeeksCount++;

          // console.log(`Week ${week} accuracy:`, weekAccuracy);
        }
      });

      const skuLinesAccuracy =
        validWeeksCount > 0 ? (linesAccuracy / validWeeksCount) * 100 : 0;
      // console.log('SKU final accuracy:', skuLinesAccuracy);

      return {
        linesAccuracy: skuLinesAccuracy,
        quantityAccuracy: 0, // We'll add these back after fixing lines accuracy
        abcAccuracy: 0,
        xyzAccuracy: 0,
        totalLines: Number(sku.order_line_count) || 0,
        totalQuantity: Number(sku.quantity_ordered) || 0,
      };
    });

    // Calculate averages across all SKUs
    const totals = accuracies.reduce(
      (acc, curr) => ({
        linesAccuracy:
          acc.linesAccuracy +
          (isNaN(curr.linesAccuracy) ? 0 : curr.linesAccuracy),
        quantityAccuracy:
          acc.quantityAccuracy +
          (isNaN(curr.quantityAccuracy) ? 0 : curr.quantityAccuracy),
        abcAccuracy:
          acc.abcAccuracy + (isNaN(curr.abcAccuracy) ? 0 : curr.abcAccuracy),
        xyzAccuracy:
          acc.xyzAccuracy + (isNaN(curr.xyzAccuracy) ? 0 : curr.xyzAccuracy),
        totalLines: acc.totalLines + curr.totalLines,
        totalQuantity: acc.totalQuantity + curr.totalQuantity,
      }),
      {
        linesAccuracy: 0,
        quantityAccuracy: 0,
        abcAccuracy: 0,
        xyzAccuracy: 0,
        totalLines: 0,
        totalQuantity: 0,
      },
    );

    // console.log('Final totals:', totals);

    const result = {
      count: validSkus.length,
      avgLinesAccuracy: validSkus.length
        ? totals.linesAccuracy / validSkus.length
        : 0,
      avgQuantityAccuracy: validSkus.length
        ? totals.quantityAccuracy / validSkus.length
        : 0,
      avgAbcAccuracy: validSkus.length
        ? totals.abcAccuracy / validSkus.length
        : 0,
      avgXyzAccuracy: validSkus.length
        ? totals.xyzAccuracy / validSkus.length
        : 0,
      totalLines: totals.totalLines,
      totalQuantity: totals.totalQuantity,
      percentage: (validSkus.length / (skuData?.length || 1)) * 100,
    };

    // console.log('Final metrics:', result);
    return result;
  };

  // Calculate filtered SKUs based on current selection
  const filteredSKUs = useMemo(() => {
    if (!skuData) return [];

    // If no filters are active, return all SKUs
    if (!selectedABC && !selectedXYZ && !selectedCategory) {
      return skuData;
    }

    return skuData.filter(sku => {
      const abc = sku.rank;
      const xyz = sku.xyz_rank;
      const combinedCategory = `${abc}${xyz}`;

      // Handle matrix cell selection (combined category)
      if (selectedCategory) {
        return combinedCategory === selectedCategory;
      }

      // Handle combined ABC-XYZ selection
      if (selectedABC && selectedXYZ) {
        return abc === selectedABC && xyz === selectedXYZ;
      }

      // Handle individual ABC selection
      if (selectedABC) {
        return abc === selectedABC;
      }

      // Handle individual XYZ selection
      if (selectedXYZ) {
        return xyz === selectedXYZ;
      }

      return true;
    });
  }, [skuData, selectedABC, selectedXYZ, selectedCategory]);

  // Handle matrix cell click
  const handleGroupClick = (abc: string, xyz: string) => {
    if (selectedABC === abc && selectedXYZ === xyz) {
      // If clicking the same cell, clear all selections
      setSelectedABC(null);
      setSelectedXYZ(null);
      setSelectedCategory(null);
    } else {
      // Set new selection
      setSelectedABC(abc);
      setSelectedXYZ(xyz);
      setSelectedCategory(`${abc}${xyz}`);
    }
  };

  console.log('Filtered SKUs:', filteredSKUs?.length, 'Selected:', {
    selectedABC,
    selectedXYZ,
    selectedCategory,
    totalSKUs: skuData?.length,
  });

  // Calculate filtered metrics based on current selection
  const filteredMetrics = useMemo(() => {
    console.log('Calculating filteredMetrics for SKUs:', filteredSKUs);
    console.log('Sample SKU data:', filteredSKUs[0]);

    const filtered = filteredSKUs.length
      ? calculateMetrics(filteredSKUs)
      : {
          count: 0,
          avgLinesAccuracy: 0,
          avgQuantityAccuracy: 0,
          avgAbcAccuracy: 0,
          avgXyzAccuracy: 0,
          totalLines: 0,
          totalQuantity: 0,
          percentage: 0,
        };

    console.log('Calculated filteredMetrics:', filtered);
    return filtered;
  }, [filteredSKUs]);

  // Calculate total metrics
  const totalMetrics = useMemo(() => calculateMetrics(skuData), [skuData]);

  // Calculate total SKUs
  const totalSKUs = skuData.length;

  // Add this before the return statement
  const groupMetrics = useMemo(() => {
    const metrics: Record<string, Record<string, GroupMetrics>> = {};

    skuData.forEach(sku => {
      const abc = sku.rank as string;
      const weeks = getWeeksFromData(sku);
      const xyz = getXYZClassification(sku, weeks);

      // Initialize group if it doesn't exist
      metrics[abc] = metrics[abc] || {};
      metrics[abc][xyz] = metrics[abc][xyz] || {
        count: 0,
        avgLinesAccuracy: 0,
        avgQuantityAccuracy: 0,
        avgAbcAccuracy: 0,
        avgXyzAccuracy: 0,
        totalLines: 0,
        totalQuantity: 0,
      };

      const group = metrics[abc][xyz];
      group.count++;

      // Calculate lines accuracy for this SKU
      let linesAccuracy = 0;
      let validWeeksCount = 0;

      weeks.forEach(week => {
        const actualLines = Number(sku[`w${week}_lines`]) || 0;
        const forecastLines = Number(sku[`w${week}_fc_lines`]) || 0;

        if (actualLines > 0) {
          const weekAccuracy =
            1 - Math.abs(actualLines - forecastLines) / actualLines;
          linesAccuracy += weekAccuracy;
          validWeeksCount++;
        }
      });

      const skuLinesAccuracy =
        validWeeksCount > 0 ? (linesAccuracy / validWeeksCount) * 100 : 0;

      // Add this SKU's metrics to the group totals
      group.avgLinesAccuracy += skuLinesAccuracy;
      group.avgQuantityAccuracy += Number(sku.quantity_accuracy) || 0;
      group.avgAbcAccuracy += Number(sku.abc_accuracy) || 0;
      group.avgXyzAccuracy += Number(sku.xyz_accuracy) || 0;
      group.totalLines += Number(sku.order_line_count) || 0;
      group.totalQuantity += Number(sku.quantity_ordered) || 0;
    });

    // Calculate averages for each group
    Object.values(metrics).forEach(xyzGroups => {
      Object.values(xyzGroups).forEach(group => {
        if (group.count > 0) {
          group.avgLinesAccuracy = group.avgLinesAccuracy / group.count;
          group.avgQuantityAccuracy = group.avgQuantityAccuracy / group.count;
          group.avgAbcAccuracy = group.avgAbcAccuracy / group.count;
          group.avgXyzAccuracy = group.avgXyzAccuracy / group.count;
        }
      });
    });

    return metrics;
  }, [skuData]);

  return (
    <div className="flex flex-col">
      <TitleSection
        id="summary-metrics"
        title={t`Item Profiling`}
        inPanelView
        collapsible
      >
        {/* title={t`Total Statistics`} */}
        <StatGroup className="flex w-full gap-1" horizontal>
          {totalMetrics.count > 0 && (
            <Stat
              title={t`Total SKUs`}
              value={formatInteger(totalMetrics.count)}
            />
          )}

          {totalMetrics.totalLines > 0 && (
            <Stat
              title={t`Total Order Lines`}
              value={formatInteger(totalMetrics.totalLines)}
            />
          )}
          {totalMetrics.totalQuantity > 0 && (
            <Stat
              title={t`Total Quantity`}
              value={formatInteger(totalMetrics.totalQuantity)}
            />
          )}
          {totalMetrics.avgLinesAccuracy > 0 &&
            !isNaN(totalMetrics.avgLinesAccuracy) && (
              <Stat
                title={t`Average Lines Accuracy`}
                value={formatInteger(totalMetrics.avgLinesAccuracy)}
                unitOfMeasure={`%`}
              />
            )}
        </StatGroup>
        <StatGroup className="flex gap-1" horizontal>
          <div className="flex-1">
            <Stat
              title={t`ABC-XYZ Classification`}
              className="h-full items-center p-4"
            >
              <SKUMatrixControl
                selectedABC={selectedABC}
                selectedXYZ={selectedXYZ}
                onABCSelect={setSelectedABC}
                onXYZSelect={setSelectedXYZ}
                onCategorySelect={setSelectedCategory}
                selectedCategory={selectedCategory}
                skuData={skuData}
                viewMode={viewMode}
              />
            </Stat>
          </div>
          <div className="flex-1">
            <Stat title={t`ABC Distribution`}>
              <div style={{ height: '300px', width: '100%' }}>
                <ResponsivePie
                  data={Object.entries(ABC_COLORS)
                    .map(([key, color]) => {
                      const count = skuData.filter(
                        sku => sku.rank === key,
                      ).length;
                      const isSelected = selectedABC === key || !selectedABC;
                      return count > 0
                        ? {
                            id: key,
                            label: key,
                            value: count,
                            color: color,
                            opacity: isSelected ? 1 : 0.3,
                          }
                        : null;
                    })
                    .filter(Boolean)}
                  onClick={({ id }) => setSelectedABC(id as string)}
                  colors={({ data }) => {
                    const color = data.color as string;
                    const opacity = data.opacity as number;
                    return color.startsWith('hsla')
                      ? color.replace(/[\d.]+\)$/, `${opacity})`)
                      : color.startsWith('hsl')
                        ? color
                            .replace('hsl', 'hsla')
                            .replace(')', `, ${opacity})`)
                        : color;
                  }}
                  margin={{ top: 40, right: 40, bottom: 40, left: 40 }}
                  enableArcLinkLabels={false}
                  fit={true}
                  padAngle={0.7}
                  cornerRadius={3}
                  innerRadius={0.5}
                  activeOuterRadiusOffset={8}
                  tooltip={({ datum }) => (
                    <ChartTooltip
                      color={[datum.color as string, datum.color as string]}
                      title={datum.id as string}
                      value={datum.value.toString()}
                      label={t`SKUs`}
                    />
                  )}
                />
              </div>
            </Stat>
          </div>
          <div className="flex-1">
            <Stat title={t`XYZ Distribution`}>
              <div style={{ height: '300px', width: '100%' }}>
                <ResponsivePie
                  data={Object.entries(XYZ_COLORS)
                    .map(([key, color]) => {
                      const count = skuData.filter(
                        sku =>
                          getXYZClassification(sku, getWeeksFromData(sku)) ===
                          key,
                      ).length;
                      const isSelected = selectedXYZ === key || !selectedXYZ;
                      return count > 0
                        ? {
                            id: key,
                            label: key,
                            value: count,
                            color: color,
                            opacity: isSelected ? 1 : 0.3,
                          }
                        : null;
                    })
                    .filter(Boolean)}
                  onClick={({ id }) => setSelectedXYZ(id as string)}
                  colors={({ data }) => {
                    const color = data.color as string;
                    const opacity = data.opacity as number;
                    return color.startsWith('hsla')
                      ? color.replace(/[\d.]+\)$/, `${opacity})`)
                      : color.startsWith('hsl')
                        ? color
                            .replace('hsl', 'hsla')
                            .replace(')', `, ${opacity})`)
                        : color;
                  }}
                  margin={{ top: 40, right: 40, bottom: 40, left: 40 }}
                  enableArcLinkLabels={false}
                  fit={true}
                  padAngle={0.7}
                  cornerRadius={3}
                  innerRadius={0.5}
                  activeOuterRadiusOffset={8}
                  tooltip={({ datum }) => (
                    <ChartTooltip
                      color={[datum.color as string, datum.color as string]}
                      title={datum.id as string}
                      value={datum.value.toString()}
                      label={t`SKUs`}
                    />
                  )}
                />
              </div>
            </Stat>
          </div>
        </StatGroup>
      </TitleSection>
      {/* Current Selection Stats */}
      {(selectedABC || selectedXYZ || selectedCategory) && (
        <StatGroup
          className={`${
            selectedCategory ? `bg-${categoryABCXYZ[selectedCategory]}` : ''
          }`}
          title={t`Selected Group Statistics`}
          subTitle={
            <>
              {`Filtered: ${
                selectedCategory
                  ? `ABCXYZ: ${selectedCategory}`
                  : `${selectedABC || 'All'}-${selectedXYZ || 'All'}`
              } `}
              <span className="">
                ({formatInteger(filteredMetrics.count)} SKUs -{' '}
                {formatInteger(filteredMetrics.percentage)}%)
              </span>
            </>
          }
        >
          <div className="flex gap-1">
            <Stat
              titleElement={
                <>
                  {`Filtered: ${
                    selectedCategory
                      ? `ABCXYZ: ${selectedCategory}`
                      : `${selectedABC || 'All'}-${selectedXYZ || 'All'}`
                  } `}
                  <span className="mx-1">
                    {` (`} {formatInteger(filteredMetrics.count)}
                    {'  SKUs - '}
                    {formatInteger(filteredMetrics.percentage)}
                    {`%) `}
                  </span>
                </>
              }
              // title={t`Selected SKUs`}
              value={formatInteger(filteredMetrics.count)}
            />

            <Stat
              title={t`Selected Order Lines`}
              value={formatInteger(filteredMetrics.totalLines)}
            />
            <Stat
              title={t`Selected Quantity`}
              value={formatInteger(filteredMetrics.totalQuantity)}
            />
            <Stat
              title={t`Average Lines Accuracy`}
              value={formatInteger(filteredMetrics.avgLinesAccuracy)}
              unitOfMeasure={`%`}
            />
          </div>
        </StatGroup>
      )}

      {/* Group Metrics */}
      <TitleSection
        id="group-metrics"
        title={t`Forecast Metrics`}
        inPanelView
        collapsible
      >
        <div className="mb-4 p-1">
          <ButtonSwitchMulti
            buttonType="minimal"
            autoSize
            options={[{ label: t`Table View` }, { label: t`Chart View` }]}
            selectedIndex={metricsView === 'table' ? 0 : 1}
            onClick={index => setMetricsView(index === 0 ? 'table' : 'chart')}
          />
        </div>
        {/* </TitleSection>
      <TitleSection
        id="forecast-metrics"
        title={t`Forecast Metrics`}
        inPanelView
        collapsible
      > */}
        {metricsView === 'table' ? (
          <StatGroup title={t`Forecast Metrics`}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableTh>{t`Group`}</TableTh>
                  <TableTh>{t`SKU Count`}</TableTh>
                  <TableTh>{t`Lines Accuracy`}</TableTh>
                  <TableTh>{t`Quantity Accuracy`}</TableTh>
                  <TableTh>{t`ABC Accuracy`}</TableTh>
                  <TableTh>{t`XYZ Accuracy`}</TableTh>
                </TableRow>
              </TableHead>
              <tbody>
                {Object.entries(groupMetrics)
                  .flatMap(([abc, xyzGroups]) =>
                    Object.entries(xyzGroups).map(([xyz, metrics]) => ({
                      abc,
                      xyz,
                      metrics,
                      percentage: (metrics.count / totalSKUs) * 100,
                    })),
                  )
                  .sort((a, b) => b.percentage - a.percentage)
                  .map(({ abc, xyz, metrics }) => (
                    <TableRow
                      key={`${abc}-${xyz}`}
                      onClick={() => handleGroupClick(abc, xyz)}
                      className={`${
                        selectedABC === abc && selectedXYZ === xyz ? '' : ''
                      }`}
                    >
                      <TableTd>{`${abc}-${xyz}`}</TableTd>
                      <TableTd>
                        {formatInteger(metrics.count)}{' '}
                        <span className="text-menu">
                          ({formatInteger((metrics.count / totalSKUs) * 100)}%)
                        </span>
                      </TableTd>
                      <TableTd>
                        {formatInteger(metrics.avgLinesAccuracy)}%
                      </TableTd>
                      <TableTd>
                        {formatInteger(metrics.avgQuantityAccuracy)}%
                      </TableTd>
                      <TableTd>
                        {formatInteger(metrics.avgAbcAccuracy)}%
                      </TableTd>
                      <TableTd>
                        {formatInteger(metrics.avgXyzAccuracy)}%
                      </TableTd>
                    </TableRow>
                  ))}
              </tbody>
            </Table>
          </StatGroup>
        ) : (
          <StatGroup title={t`Forecast Performance`}>
            <div style={{ height: '400px' }}>
              <ResponsiveBar
                data={Object.entries(groupMetrics).flatMap(([abc, xyzGroups]) =>
                  Object.entries(xyzGroups).map(([xyz, metrics]) => ({
                    group: `${abc}-${xyz}`,
                    'Lines Accuracy': metrics.avgLinesAccuracy || 0,
                    'Quantity Accuracy': metrics.avgQuantityAccuracy || 0,
                    'ABC Accuracy': metrics.avgAbcAccuracy || 0,
                    'XYZ Accuracy': metrics.avgXyzAccuracy || 0,
                  })),
                )}
                keys={[
                  'Lines Accuracy',
                  'Quantity Accuracy',
                  'ABC Accuracy',
                  'XYZ Accuracy',
                ]}
                indexBy="group"
                margin={{ top: 50, right: 130, bottom: 50, left: 60 }}
                padding={0.3}
                valueScale={{ type: 'linear' }}
                indexScale={{ type: 'band', round: true }}
                colors={{ scheme: 'nivo' }}
                axisBottom={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: -45,
                  legend: t`Group`,
                  legendPosition: 'middle',
                  legendOffset: 40,
                }}
                axisLeft={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: 0,
                  legend: t`Accuracy (%)`,
                  legendPosition: 'middle',
                  legendOffset: -40,
                }}
                labelSkipWidth={12}
                labelSkipHeight={12}
                legends={[
                  {
                    dataFrom: 'keys',
                    anchor: 'bottom-right',
                    direction: 'column',
                    justify: false,
                    translateX: 120,
                    translateY: 0,
                    itemsSpacing: 2,
                    itemWidth: 100,
                    itemHeight: 20,
                    itemDirection: 'left-to-right',
                    itemOpacity: 0.85,
                    symbolSize: 20,
                  },
                ]}
              />
            </div>
          </StatGroup>
        )}
      </TitleSection>
      <TitleSection id="sku-list" title={t`SKU List`} inPanelView collapsible>
        <SKUListView filteredSKUs={filteredSKUs} />
      </TitleSection>
    </div>
  );
};

export default SKUGroupAnalysisView;
